import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import CropIcon from '@mui/icons-material/Crop';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import AddIcon from '@mui/icons-material/Add';
import ClipLabelFields, { Draft, EMPTY_CLIP_DRAFT, clipLabelEditFrom } from './ClipLabelFields';
import { brollSegmentId, brollSourceClipFrom } from '../brollAssets';
import {
  VideoClip, PreviewStatus, ClipSegment, BrollAsset, ClipCategory, ClipLabel, ClipLabelEdit,
  ClipLabelVocabulary, LibraryClipLabel, NewClipLabel,
} from 'services/VideoEditingService';
import RangeTrimmer from './RangeTrimmer';
import SaveClipDialog from './SaveClipDialog';
import ClipLabelEditor from './ClipLabelEditor';
import CachedRangePreviewFrame, { RANGE_PREVIEW_TRACK_HEIGHT, sourceRangePreviewFrames, sourceThumbFrame, RangePreviewFrame } from './CachedRangePreviewFrame';
import PreviewPlaybackOverlay from './PreviewPlaybackOverlay';
import TimelineZoomControls, { DEFAULT_TIMELINE_ZOOM } from './TimelineZoomControls';
import { formatDuration, formatBytes } from '../format';
import { STAGE_PLAYER_HEIGHT } from '../layout';
import { useRangeScrubber } from '../useRangeScrubber';

// What the capture UI hands up to save a new segment.
export interface NewClipSegment {
  sourceFileId: string;
  fileName: string;
  kind: 'video' | 'audio';
  inSec: number;
  outSec: number;
  name: string | null;
}

export interface LinkedTimelineClip {
  clipId: string;
  sourceFileId: string;
  segmentId?: string;
  inSec: number;
  outSec: number;
  kind: 'video' | 'audio';
  // The editor note on this clip (rendered into the instructions). Edited from
  // the clip-editor modal's note panel.
  note?: string;
}

interface ClipBrowserProps {
  clips: VideoClip[];
  selectedFileId: string | null;
  selectedSegmentId?: string | null;
  onSelect: (clip: VideoClip) => void;
  onSelectSegment?: (segment: ClipSegment) => void;
  // Ref to the preview player — the segment capture reads its playhead.
  playerRef?: React.RefObject<HTMLVideoElement>;
  // Per-clip transcode status (video clips only).
  previewStatuses?: Record<string, PreviewStatus>;
  previewErrors?: Record<string, string>;
  onRetryPreview?: (clip: VideoClip) => Promise<void>;
  // Split the selected video clip's audio into its own audio file (backend
  // creates "<name> (audio).m4a" in the Drive folder; the parent surfaces it in
  // the clip list). Resolves with what happened so the toast can say it.
  onExtractAudio?: (clip: VideoClip) => Promise<{ clip: VideoClip; alreadyExisted: boolean; transcriptCopied: boolean } | null>;
  // Source fileIds already used on the edit timeline — flagged with a badge.
  usedFileIds?: string[];
  // Saved sub-clip ids already used on the timeline.
  usedSegmentIds?: string[];
  // Add a clip to the edit timeline (video → video track, audio → audio track).
  onAdd?: (clips: VideoClip[]) => void;
  onRemoveFromTimeline?: (clip: VideoClip) => boolean;
  // Remove a clip from the active plan's working set. The parent removes its
  // timeline copy in the same undoable change; the Drive file is never deleted.
  // Saved sub-clips for this folder, listed under their source clips.
  segments?: ClipSegment[];
  onSaveSegment?: (seg: NewClipSegment) => Promise<boolean>;
  onDeleteSegment?: (segmentId: string) => Promise<boolean>;
  // Resolves to whether the clip actually landed on the timeline — it can fail if the segment's
  // source file can't be resolved, and saying "Added" when nothing was is worse than saying so.
  onAddSegment?: (seg: ClipSegment) => void | boolean | Promise<boolean>;
  onRemoveSegmentFromTimeline?: (seg: ClipSegment) => boolean;
  // The timeline instance linked to the selected source clip. Its saved trim is
  // the starting range here, and applying Trim writes back to that instance.
  timelineClip?: LinkedTimelineClip | null;
  onTrimSelected?: (clip: VideoClip, inSec: number, outSec: number) => boolean;
  onTrimSegment?: (segment: ClipSegment, inSec: number, outSec: number) => Promise<boolean>;
  // Unified-workspace slots: render the clip list into `library` (the page's
  // left rail) and the preview/trim card into `inspector` (the shared stage).
  // Absent → the original side-by-side layout renders inline.
  slots?: { library?: HTMLElement | null; inspector?: HTMLElement | null };
  // Optional panel rendered to the RIGHT of the inspector's video player (they
  // share a row; the video keeps the majority of the width). Used by the clip
  // editor modal to host the "Note for editor" field.
  notePanel?: React.ReactNode;

  // AI clip labels for the whole folder, keyed by Drive fileId. Omitted entirely
  // when the page hasn't loaded them, in which case the labels panel is hidden.
  clipLabels?: Record<string, ClipLabel[]>;
  clipLabelVocabulary?: ClipLabelVocabulary | null;
  clipLabelsLoading?: boolean;
  onSaveClipLabel?: (labelId: string, edit: ClipLabelEdit) => Promise<void>;

  // The human category vocabulary, plus a way to add to it. Both optional: without them
  // the label editor simply doesn't offer the category field.
  clipCategories?: ClipCategory[] | null;
  onCreateClipCategory?: (name: string) => Promise<ClipCategory>;
  // Put the currently-trimmed range into the b-roll library as its own clip. Omitted
  // hides the button, so a page that hasn't wired it up looks exactly as it did.
  onAddToBrollLibrary?: (input: NewClipLabel) => Promise<void>;

  // ── The B-roll tab: library clips pulled into this project ──
  // Assets, not placements: having one here makes it available to drag onto a track, it does
  // not put it in the edit. Each is backed by an ordinary ClipSegment (in `segments`), which is
  // what makes it drag, place and trim exactly like a sub-clip carved from a project file.
  brollAssets?: BrollAsset[];
  // The resolved library record for each asset, re-fetched by the page because the poster and
  // stream URLs are signed and expire. An asset with no record here is one the library can no
  // longer resolve, and its card says so instead of quietly disappearing.
  brollLabels?: LibraryClipLabel[];
  onOpenBrollPicker?: () => void;
  onRemoveBrollAsset?: (labelId: string) => void;
  // Edit the clip in the LIBRARY — name, tags, range — which every project using it sees.
  // Trimming in the inspector edits only this project's copy.
  onEditBrollLibraryClip?: (label: LibraryClipLabel) => void;
}

// dataTransfer type carrying a dragged clip's Drive fileId → the timeline's
// drop handler looks the clip up and adds it to the hovered track.
export const CLIP_DRAG_MIME = 'application/x-sbly-clip';
// A saved sub-clip drags as its own type: the drop needs the segment's trim, not
// just its source file, so the two payloads must not be confused for each other.
export const SEGMENT_DRAG_MIME = 'application/x-sbly-clip-segment';
// Marker set alongside either payload when what's being dragged is audio, so the
// timeline can tell during DRAGOVER — the only phase where it matters, since that is
// when the drop target is highlighted, and dataTransfer.getData() is deliberately
// blank until the drop. The kind has to be in the type list or it cannot be read.
export const AUDIO_DRAG_MIME = 'application/x-sbly-audio';

// Seconds → compact tenth-of-a-second label for segment ranges.
const secLabel = (s: number) => `${s.toFixed(1)}s`;

const thumbFrameFor = sourceThumbFrame;
const thumbFrameForSegment = (seg: ClipSegment, source: VideoClip): RangePreviewFrame => ({
  id: `${source.fileId}:seg:${seg.segmentId}:thumb`,
  src: source.streamUrl,
  sourceTimeSec: Math.max(0.1, (seg.inSec + seg.outSec) / 2),
  sourceId: source.fileId,
  sourceDurationSec: source.durationSec ?? undefined,
  width: source.width,
  height: source.height,
});
function ClipThumb({ frame, loadEnabled }: { frame: RangePreviewFrame; loadEnabled: boolean }) {
  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      <CachedRangePreviewFrame frame={frame} loadEnabled={loadEnabled} />
    </Box>
  );
}

// Cards flex to fit exactly 3 per row (two 8px gaps between them). Source and
// sub-clip cards share the width; they're told apart by the scissors icon and
// the tinted group container, not size.
const CARD_BASIS = 'calc((100% - 16px) / 3)';

// Minimal "on timeline" marker: a small filled check disc in the corner (the
// green card border carries the rest of the signal). Clean over any thumbnail.
function OnTimelineBadge() {
  return (
    <Tooltip title="On the timeline">
      <Box sx={{ position: 'absolute', top: 6, left: 6, width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#16a34a', boxShadow: '0 1px 4px rgba(0,0,0,0.35)' }}>
        <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
      </Box>
    </Tooltip>
  );
}

// While a clip's preview is still transcoding, dim the card and show a spinner;
// a failed transcode shows a short note (retry lives in the clip editor).
function TranscodeOverlay({ isVideo, status }: { isVideo: boolean; status?: PreviewStatus }) {
  if (!isVideo || !status || status === 'ready') return null;
  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, backgroundColor: 'rgba(15,23,42,0.55)' }}>
      {status === 'error'
        ? <Typography sx={{ fontSize: 10, color: '#fecaca', textAlign: 'center', px: 1 }}>Preview failed</Typography>
        : (<><CircularProgress size={22} sx={{ color: '#fff' }} /><Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.85)' }}>Preparing…</Typography></>)}
    </Box>
  );
}

// Deleting a saved sub-clip and dropping a borrowed library clip look the same in the editor —
// one trash can — but they are different acts on different things, so they confirm differently
// and run through different callbacks.
type RemoveTarget =
  | { kind: 'segment'; segment: ClipSegment }
  | { kind: 'broll'; segment: ClipSegment; labelId: string };

// Small green badge marking a clip that's already on the edit timeline.
function OnTimelineChip() {
  return (
    <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="On timeline"
      sx={{ '& .MuiChip-icon': { fontSize: 15 } }} />
  );
}

// Left: a scrollable list of the folder's clips. Right: a native player for the
// selected clip, pointed at its signed stream URL (the backend proxies the bytes
// from Drive with Range support, so the scrubber seeks). `key={streamUrl}` forces
// the <video> to remount when the selection changes rather than reusing a stale
// media element.
export default function ClipBrowser({
  clips, selectedFileId, selectedSegmentId, onSelect, onSelectSegment, playerRef, previewStatuses, previewErrors, onRetryPreview, onExtractAudio, usedFileIds, usedSegmentIds, onAdd, onRemoveFromTimeline,
  segments, onSaveSegment, onDeleteSegment, onAddSegment, onRemoveSegmentFromTimeline, timelineClip, onTrimSelected, onTrimSegment, slots, notePanel,
  clipLabels, clipLabelVocabulary, clipLabelsLoading, onSaveClipLabel,
  clipCategories, onCreateClipCategory, onAddToBrollLibrary,
  brollAssets, brollLabels, onOpenBrollPicker, onRemoveBrollAsset, onEditBrollLibraryClip,
}: ClipBrowserProps) {
  const internalPlayerRef = useRef<HTMLVideoElement>(null);
  const previewPlayerRef = playerRef || internalPlayerRef;
  // Library b-roll borrowed into this project. Its source files are deliberately absent from
  // `clips` (the project doesn't own them), so they are resolved here as well — otherwise
  // selecting one, from its card or by double-clicking its placement on the timeline, opens an
  // empty inspector.
  const brollLabelsById = useMemo(
    () => new Map((brollLabels || []).map(l => [l.labelId, l])),
    [brollLabels],
  );
  const brollLabelsByFileId = useMemo(
    () => new Map((brollLabels || []).map(l => [l.fileId, l])),
    [brollLabels],
  );
  // Set when what's selected is borrowed from the library rather than owned by the project. Its
  // label edits are shared with every other project using it, which changes what the inspector
  // may offer.
  const selectedBrollLabel = selectedFileId ? brollLabelsByFileId.get(selectedFileId) || null : null;
  const selected = clips.find(c => c.fileId === selectedFileId)
    || (selectedBrollLabel ? brollSourceClipFrom(selectedBrollLabel) : null);
  const selectedSegment = (segments || []).find(segment => segment.segmentId === selectedSegmentId) || null;
  const linkedTimelineClip = timelineClip
    && timelineClip.sourceFileId === selected?.fileId
    && (selectedSegmentId ? timelineClip.segmentId === selectedSegmentId : !timelineClip.segmentId)
    ? timelineClip
    : null;
  const usedSet = useMemo(() => new Set(usedFileIds || []), [usedFileIds]);
  const usedSegmentSet = useMemo(() => new Set(usedSegmentIds || []), [usedSegmentIds]);
  const [snack, setSnack] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const [removing, setRemoving] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [readyPreviewSource, setReadyPreviewSource] = useState<string | null>(null);
  const [clipZoom, setClipZoom] = useState(DEFAULT_TIMELINE_ZOOM);
  const [libraryTab, setLibraryTab] = useState<'video' | 'audio' | 'broll'>('video');
  const [retryingPreview, setRetryingPreview] = useState(false);
  const [extractingAudio, setExtractingAudio] = useState(false);

  const [segName, setSegName] = useState('');
  const [segSaving, setSegSaving] = useState(false);
  const [trimSaving, setTrimSaving] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Adding to the b-roll library is a separate flow from saving a timeline clip: it names
  // and categorises the range for people searching the library later, and touches nothing
  // in the edit. Its own state so neither dialog can half-fill the other.
  const [brollDialogOpen, setBrollDialogOpen] = useState(false);
  // The SAME fields the library's own editor collects, so a clip filed from here arrives as
  // findable as one filed there. They were a name and a product; everything else an editor
  // would search by — what it shows, keywords, the description — had to be added later from
  // the other page, which in practice meant never.
  const [brollDraft, setBrollDraft] = useState<Draft>(EMPTY_CLIP_DRAFT);
  const [brollSaving, setBrollSaving] = useState(false);
  const [brollError, setBrollError] = useState<string | null>(null);
  const knownDuration = selected?.durationSec != null && isFinite(selected.durationSec) ? selected.durationSec : null;
  const initialInSec = linkedTimelineClip?.inSec ?? selectedSegment?.inSec ?? 0;
  const initialOutSec = linkedTimelineClip?.outSec ?? selectedSegment?.outSec ?? knownDuration;
  const rangeResetKey = `${selectedFileId || ''}:${selectedSegmentId || linkedTimelineClip?.clipId || 'source'}:${initialInSec}:${initialOutSec ?? ''}`;
  const {
    mediaDuration: mediaDur,
    inSec: trimIn,
    outSec: trimOut,
    playheadSec: playheadT,
    loadDuration,
    changeRange: handleTrimChange,
    seek: seekTrimPlayhead,
  } = useRangeScrubber({
    playerRef: previewPlayerRef,
    resetKey: rangeResetKey,
    knownDuration,
    initialInSec,
    initialOutSec,
  });
  const rangePreviewFrames = useMemo(() => {
    if (!selected?.mimeType.startsWith('video/') || !mediaDur || mediaDur <= 0) return [];
    return sourceRangePreviewFrames(selected.fileId, selected.streamUrl, mediaDur, selected.width, selected.height);
  }, [mediaDur, selected]);
  const selectedPreviewSource = selected ? `${selected.fileId}:${selected.streamUrl}` : null;
  const previewCanPlay = selectedPreviewSource != null && readyPreviewSource === selectedPreviewSource;

  useEffect(() => {
    setSegName('');
    setNameDialogOpen(false);
    setSaveError(null);
    setPreviewPlaying(false);
  }, [selectedFileId, selectedSegmentId]);

  const togglePreviewPlayback = () => {
    const player = previewPlayerRef.current;
    if (!player) return;
    if (player.paused) void player.play();
    else player.pause();
  };

  async function retrySelectedPreview() {
    if (!selected || !onRetryPreview || retryingPreview) return;
    setRetryingPreview(true);
    try {
      await onRetryPreview(selected);
      setSnack(`Retrying preview for “${selected.name}”`);
    } catch {
      setSnack(`Could not retry preview for “${selected.name}”`);
    } finally {
      setRetryingPreview(false);
    }
  }

  async function extractSelectedAudio() {
    if (!selected || !onExtractAudio || extractingAudio) return;
    setExtractingAudio(true);
    try {
      const res = await onExtractAudio(selected);
      if (res) {
        setSnack(res.alreadyExisted
          ? `“${res.clip.name}” already exists — selected it`
          : `Audio saved as “${res.clip.name}”${res.transcriptCopied ? ' — reusing the video’s transcript' : ''}`);
      }
    } catch (err: any) {
      setSnack(err?.response?.data?.error || err?.message || 'Could not extract the audio');
    } finally {
      setExtractingAudio(false);
    }
  }

  // Add + confirm with a toast (the per-clip "On timeline" badge is the persistent signal).
  const handleAdd = (cs: VideoClip[]) => {
    if (!onAdd || cs.length === 0) return;
    const addable = cs.filter((clip) => !usedSet.has(clip.fileId));
    if (addable.length === 0) {
      setSnack(cs.length === 1 ? `“${cs[0].name}” is already on the timeline` : 'All clips are already on the timeline');
      return;
    }
    onAdd(addable);
    setSnack(addable.length === 1 ? `Added “${addable[0].name}” to the timeline` : `Added ${addable.length} clips to the timeline`);
  };

  function removeSourceFromTimeline(clip: VideoClip) {
    const removed = onRemoveFromTimeline?.(clip) ?? false;
    setSnack(removed ? `Removed “${clip.name}” from the timeline — use Timeline Undo to restore it` : `“${clip.name}” is not on the timeline`);
  }

  function removeSavedClipFromTimeline(segment: ClipSegment) {
    const removed = onRemoveSegmentFromTimeline?.(segment) ?? false;
    setSnack(removed ? `Removed “${segment.name || 'saved clip'}” from the timeline — use Timeline Undo to restore it` : `“${segment.name || 'Saved clip'}” is not on the timeline`);
  }

  // One place both "add to timeline" affordances go through, so neither can report a success the
  // other wouldn't.
  async function addSegmentToTimeline(seg: ClipSegment) {
    const name = seg.name || 'saved clip';
    const added = await onAddSegment?.(seg);
    setSnack(added === false
      ? `Could not add “${name}” to the timeline`
      : `Added “${name}” to the timeline`);
  }

  async function confirmRemoval() {
    if (!removeTarget || removing) return;
    setRemoving(true);
    try {
      const { segment } = removeTarget;
      if (removeTarget.kind === 'broll') {
        // Removes the asset, its segment and every placement together. Deleting only the
        // segment (what the saved-clip path does) would leave the asset behind, showing on the
        // B-Roll tab as an unavailable clip nobody asked to keep.
        onRemoveBrollAsset?.(removeTarget.labelId);
        setSnack(`Removed “${segment.name || 'clip'}” from this project`);
        setRemoveTarget(null);
        return;
      }
      const deleted = await onDeleteSegment?.(segment.segmentId);
      if (deleted) setSnack(`Deleted “${segment.name || 'saved clip'}”`);
      else setSnack(`Could not delete “${segment.name || 'saved clip'}”`);
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  }

  async function saveSegment() {
    if (!onSaveSegment || !selected || trimOut == null || trimOut <= trimIn) return;
    setSegSaving(true);
    setSaveError(null);
    try {
      const ok = await onSaveSegment({
        sourceFileId: selected.fileId,
        fileName: selected.name,
        kind: selected.mimeType.startsWith('audio/') ? 'audio' : 'video',
        inSec: Math.round(trimIn * 100) / 100,
        outSec: Math.round(trimOut * 100) / 100,
        name: segName.trim() || null,
      });
      if (!ok) throw new Error('Save clip request failed');
      setNameDialogOpen(false);
      setSnack('New reusable clip saved and added to the timeline');
      setSegName('');
    } catch (error) {
      console.error('Failed to save source clip range', error);
      setSaveError('Failed to save the new clip');
    } finally {
      setSegSaving(false);
    }
  }

  // Put the trimmed range into the b-roll library, so a good moment spotted while editing is
  // findable later instead of being re-found by scrubbing the same take again. Independent of
  // the timeline: adding to the library neither adds to nor changes the edit.
  // The range to file: whatever the trimmer currently shows, or the whole clip when nothing
  // has been trimmed — the button is reachable before the trimmer has loaded a duration, and
  // "this whole clip is useful" is a perfectly ordinary thing to want to say.
  // Product options for the add-to-library dialog, and what the labeler already said about
  // this clip — so the field is prefilled rather than asking a question already answered.
  const brollProductOptions = useMemo(
    () => (clipLabelVocabulary?.dimensions || [])
      .find(d => d.key === 'productCategory')?.values.map(v => v.value) || [],
    [clipLabelVocabulary],
  );
  const labelledProduct = selected
    ? (clipLabels?.[selected.fileId] || []).find(l => l.scope === 'clip')?.productCategory ?? null
    : null;

  const brollRange = (() => {
    if (!selected) return null;
    if (trimOut != null && trimOut > trimIn + 0.05) {
      return { inSec: Math.round(trimIn * 100) / 100, outSec: Math.round(trimOut * 100) / 100, whole: false };
    }
    const duration = mediaDur ?? selected.durationSec;
    if (!duration || duration <= 0.2) return null; // no known duration -> nothing safe to send
    return { inSec: 0, outSec: Math.round(duration * 100) / 100, whole: true };
  })();

  async function addToBrollLibrary() {
    if (!onAddToBrollLibrary || !selected || !brollRange) return;
    setBrollSaving(true);
    setBrollError(null);
    try {
      await onAddToBrollLibrary({
        fileId: selected.fileId,
        inSec: brollRange.inSec,
        outSec: brollRange.outSec,
        creationSource: 'video_editor',
        // Shared with the library's own editor, so the product→SKU invalidation and the
        // "never send categories a surface didn't offer" rule hold here too.
        ...clipLabelEditFrom(brollDraft, { categoriesOffered: !!clipCategories }),
      });
      setBrollDialogOpen(false);
      setBrollDraft(EMPTY_CLIP_DRAFT);
      setSnack('Added to the B-Roll Library');
    } catch (error: any) {
      // The useful failures here are specific ("open its folder in the Video Editing tool
      // first"), so show what the backend said rather than a generic message.
      setBrollError(error?.response?.data?.error || error?.message || 'Could not add it to the library');
    } finally {
      setBrollSaving(false);
    }
  }

  async function trimCurrentClip() {
    if (!selected || trimOut == null) return;
    const inSec = Math.round(trimIn * 100) / 100;
    const outSec = Math.round(trimOut * 100) / 100;
    if (selectedSegment && onTrimSegment) {
      setTrimSaving(true);
      try {
        const ok = await onTrimSegment(selectedSegment, inSec, outSec);
        setSnack(ok ? 'Saved clip updated' : 'Could not update the saved clip');
      } finally {
        setTrimSaving(false);
      }
      return;
    }
    if (!onTrimSelected) return;
    const wasOnTimeline = usedSet.has(selected.fileId);
    const ok = onTrimSelected(selected, inSec, outSec);
    setSnack(ok
      ? (wasOnTimeline ? 'Timeline clip trimmed' : 'Clip added to the timeline and trimmed')
      : 'Could not trim this clip');
  }

  const slotted = !!slots;

  // One clip card: a portrait thumbnail with the name/meta overlaid and a single
  // add/remove-from-timeline action. Clicking the body selects it (opens the
  // editor); dragging drops it onto a timeline track. No delete — every Drive
  // clip stays findable.
  const clipCard = (clip: VideoClip) => {
    const onTimeline = usedSet.has(clip.fileId);
    const isVideo = clip.mimeType.startsWith('video/');
    const status = previewStatuses?.[clip.fileId];
    return (
      <Box
        key={clip.fileId}
        onClick={() => onSelect(clip)}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(CLIP_DRAG_MIME, clip.fileId);
          if (clip.mimeType.startsWith('audio/')) e.dataTransfer.setData(AUDIO_DRAG_MIME, clip.fileId);
          e.dataTransfer.setData('text/plain', clip.name);
          e.dataTransfer.effectAllowed = 'copy';
        }}
        sx={{
          flex: `0 0 ${CARD_BASIS}`, maxWidth: CARD_BASIS, minWidth: 0, cursor: 'grab', borderRadius: 1.5, overflow: 'hidden', position: 'relative',
          // No selection outline — clicking a card opens its editor, so a
          // persistent purple ring added noise. On-timeline (green) still shows.
          border: '2px solid', borderColor: onTimeline ? '#16a34a' : 'divider',
          backgroundColor: '#0f172a',
          '&:hover': { borderColor: onTimeline ? '#16a34a' : '#94a3b8' },
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: clip.width && clip.height ? `${clip.width} / ${clip.height}` : '9 / 16', maxHeight: 220, backgroundColor: '#111827' }}>
          {isVideo ? (
            <ClipThumb frame={thumbFrameFor(clip)} loadEnabled={status === 'ready'} />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
              <AudiotrackIcon sx={{ fontSize: 40 }} />
            </Box>
          )}
          <TranscodeOverlay isVideo={isVideo} status={status} />
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, px: 0.75, py: 0.5, background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 65%)' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {clip.name}
            </Typography>
            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>{formatDuration(clip.durationSec)}</Typography>
          </Box>
          {onTimeline && <OnTimelineBadge />}
          {onAdd && !onTimeline && (
            <Tooltip title="Add to timeline">
              <IconButton aria-label={`Add ${clip.name} to timeline`} size="small"
                sx={{ position: 'absolute', top: 2, right: 2, color: '#fff', backgroundColor: 'rgba(15,23,42,0.65)', '&:hover': { backgroundColor: 'rgba(79,70,229,0.9)' } }}
                onClick={(e) => { e.stopPropagation(); handleAdd([clip]); }}>
                <PlaylistAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onRemoveFromTimeline && onTimeline && (
            <Tooltip title="Remove from timeline">
              <IconButton aria-label={`Remove ${clip.name} from timeline`} size="small"
                sx={{ position: 'absolute', top: 2, right: 2, color: '#fff', backgroundColor: 'rgba(15,23,42,0.65)', '&:hover': { backgroundColor: 'rgba(127,29,29,0.9)' } }}
                onClick={(e) => { e.stopPropagation(); removeSourceFromTimeline(clip); }}>
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  // One saved-sub-clip card. Rendered in the same group as its source clip.
  //
  // `opts` is what lets a borrowed library clip render as exactly the same card: its source file
  // is not in `clips` (the project doesn't own it) and it has no transcode status, so both have
  // to be handed in. Everything else — the drag payload, the on-timeline badge, add and remove —
  // is shared deliberately, because the two kinds of clip should behave the same.
  const segmentCard = (seg: ClipSegment, opts?: {
    source?: VideoClip;
    // Shown instead of the frame-grabbed thumbnail. Library clips have a still generated at
    // their in-point, and no preview status to unlock the grabber with.
    posterUrl?: string | null;
    // Replaces the delete-saved-clip action; deleting a b-roll's segment would orphan its asset.
    onRemove?: () => void;
    removeTitle?: string;
    // null = not clickable. The inspector resolves the clip through the project's own list, so
    // a borrowed clip has nothing to open.
    onOpen?: (() => void) | null;
  }) => {
    const source = opts?.source || clips.find((c) => c.fileId === seg.sourceFileId);
    const poster = opts?.posterUrl || null;
    const open = opts?.onOpen === undefined ? () => onSelectSegment?.(seg) : opts.onOpen;
    const onTimeline = usedSegmentSet.has(seg.segmentId);
    return (
      <Box
        key={seg.segmentId}
        {...(open ? {
          role: 'button',
          tabIndex: 0,
          'aria-label': `Edit saved clip ${seg.name || 'saved clip'}`,
          onClick: open,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); }
          },
        } : {})}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(SEGMENT_DRAG_MIME, seg.segmentId);
          if (seg.kind === 'audio') e.dataTransfer.setData(AUDIO_DRAG_MIME, seg.segmentId);
          e.dataTransfer.setData('text/plain', seg.name || 'saved clip');
          e.dataTransfer.effectAllowed = 'copy';
        }}
        sx={{
          flex: `0 0 ${CARD_BASIS}`, maxWidth: CARD_BASIS, minWidth: 0, cursor: 'grab', borderRadius: 1.5, overflow: 'hidden', position: 'relative',
          // No selection outline (see clipCard) — on-timeline (green) still shows.
          border: '2px solid', borderColor: onTimeline ? '#16a34a' : 'divider', backgroundColor: '#0f172a',
          '&:hover': { borderColor: onTimeline ? '#16a34a' : '#94a3b8' },
          '&:focus-visible': { outline: '2px solid #475569', outlineOffset: 1 },
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: source?.width && source?.height ? `${source.width} / ${source.height}` : '9 / 16', maxHeight: 220, backgroundColor: '#111827' }}>
          {poster ? (
            <Box
              component="img"
              src={poster}
              alt={seg.name || 'clip preview'}
              loading="lazy"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : source && source.mimeType.startsWith('video/') ? (
            <ClipThumb frame={thumbFrameForSegment(seg, source)} loadEnabled={previewStatuses?.[source.fileId] === 'ready'} />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
              <AudiotrackIcon sx={{ fontSize: 40 }} />
            </Box>
          )}
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, px: 0.75, py: 0.5, background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 65%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ContentCutIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {seg.name || 'Saved clip'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
              {secLabel(seg.inSec)}–{secLabel(seg.outSec)} · {(seg.outSec - seg.inSec).toFixed(1)}s
            </Typography>
          </Box>
          {onTimeline && <OnTimelineBadge />}
          {onAddSegment && !onTimeline && (
            <Tooltip title="Add to timeline">
              <IconButton aria-label={`Add ${seg.name || 'saved clip'} to timeline`} size="small"
                sx={{ position: 'absolute', top: 2, right: 2, color: '#fff', backgroundColor: 'rgba(15,23,42,0.65)', '&:hover': { backgroundColor: 'rgba(79,70,229,0.9)' } }}
                onClick={(event) => { event.stopPropagation(); void addSegmentToTimeline(seg); }}>
                <PlaylistAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onRemoveSegmentFromTimeline && onTimeline && (
            <Tooltip title="Remove from timeline">
              <IconButton aria-label={`Remove ${seg.name || 'saved clip'} from timeline`} size="small"
                sx={{ position: 'absolute', top: 2, right: 2, color: '#fff', backgroundColor: 'rgba(15,23,42,0.65)', '&:hover': { backgroundColor: 'rgba(127,29,29,0.9)' } }}
                onClick={(event) => { event.stopPropagation(); removeSavedClipFromTimeline(seg); }}>
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {opts?.onRemove && (
            <Tooltip title={opts.removeTitle || 'Remove'}>
              <IconButton aria-label={`Remove ${seg.name || 'clip'}`} size="small"
                sx={{ position: 'absolute', top: 2, right: 32, color: '#fff', backgroundColor: 'rgba(15,23,42,0.65)', '&:hover': { backgroundColor: 'rgba(127,29,29,0.9)' } }}
                onClick={(event) => { event.stopPropagation(); opts.onRemove?.(); }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* Saved clips are user-created, so they can be deleted directly from
              the card (source Drive clips cannot — they stay findable). */}
          {!opts?.onRemove && onDeleteSegment && (
            <Tooltip title="Delete saved clip">
              <IconButton aria-label={`Delete saved clip ${seg.name || 'saved clip'}`} size="small"
                sx={{ position: 'absolute', top: 2, right: 32, color: '#fff', backgroundColor: 'rgba(15,23,42,0.65)', '&:hover': { backgroundColor: 'rgba(127,29,29,0.9)' } }}
                onClick={(event) => { event.stopPropagation(); setRemoveTarget({ kind: 'segment', segment: seg }); }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  // Clip library, split into Video / Audio tabs. Within a tab, cards pack
  // densely; a source clip that has sub-clips carved from it is wrapped with
  // them in a tinted container headed by the source name, so the derived clips
  // read as belonging to that source. Sources with no sub-clips are bare cards.
  const videoClips = clips.filter((c) => c.mimeType.startsWith('video/'));
  const audioClips = clips.filter((c) => c.mimeType.startsWith('audio/'));
  const tabClips = libraryTab === 'audio' ? audioClips : videoClips;
  const librarySection = (
      <Box sx={{ width: slotted ? '100%' : { xs: '100%', md: 360 }, flexShrink: 0 }}>
        <Tabs
          value={libraryTab}
          onChange={(_e, v) => setLibraryTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 34, mb: 1,
            // Stay pinned to the top of the scrolling clip rail.
            position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff',
            '& .MuiTab-root': { minHeight: 34, textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab value="video" label={`Video (${videoClips.length})`} />
          <Tab value="audio" label={`Audio (${audioClips.length})`} />
          <Tab value="broll" label={`B-Roll (${(brollAssets || []).length})`} />
        </Tabs>
        {libraryTab === 'broll' ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1 }}>
            <Button
              size="small"
              fullWidth
              variant="outlined"
              startIcon={<AddIcon fontSize="small" />}
              onClick={onOpenBrollPicker}
              disabled={!onOpenBrollPicker}
              sx={{ textTransform: 'none' }}
            >
              Add From Library
            </Button>
            {(brollAssets || []).length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                No b-roll pulled into this project yet. “Add From Library” searches the whole
                library, and also takes a B-roll ID copied from a clip there.
              </Typography>
            )}
            {(brollAssets || []).map((asset) => {
              // Each asset has a matching ClipSegment, and the card IS the ordinary segment card:
              // it drags that segment, so a library clip lands on a track with its range applied
              // and looks and behaves exactly like a sub-clip of a project file.
              const segment = (segments || []).find(s => s.segmentId === brollSegmentId(asset.labelId));
              const label = brollLabelsById.get(asset.labelId);
              // Without its library record there is no stream to place and no still to show, so
              // say so rather than offering a card that would silently do nothing.
              if (!segment || !label) {
                return (
                  <Box
                    key={asset.labelId}
                    sx={{
                      flex: `0 0 ${CARD_BASIS}`, maxWidth: CARD_BASIS, minWidth: 0, p: 1,
                      borderRadius: 1.5, border: '1px dashed', borderColor: 'divider',
                      display: 'flex', flexDirection: 'column', gap: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VideoLibraryIcon fontSize="small" sx={{ color: '#92400E' }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {asset.name || asset.fileName || asset.labelId}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">unavailable</Typography>
                    {onRemoveBrollAsset && (
                      <Button size="small" onClick={() => onRemoveBrollAsset(asset.labelId)} sx={{ alignSelf: 'flex-start' }}>
                        Remove
                      </Button>
                    )}
                  </Box>
                );
              }
              return segmentCard(segment, {
                source: brollSourceClipFrom(label),
                posterUrl: label.posterUrl,
                onRemove: onRemoveBrollAsset ? () => onRemoveBrollAsset(asset.labelId) : undefined,
                removeTitle: 'Remove from this project (the library keeps the clip)',
              });
            })}
          </Box>
        ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1 }}>
          {tabClips.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
              No {libraryTab} clips in this project yet.
            </Typography>
          )}
          {tabClips.map((clip) => {
            const clipSegments = (segments || []).filter((s) => s.sourceFileId === clip.fileId);
            if (clipSegments.length === 0) return clipCard(clip);
            return (
              <Box
                key={`group-${clip.fileId}`}
                sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 1, p: 1, borderRadius: 1.5, backgroundColor: '#f1f5f9', border: '1px solid', borderColor: 'divider' }}
              >
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {clip.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                    + {clipSegments.length} saved
                  </Typography>
                </Box>
                {clipCard(clip)}
                {clipSegments.map((s) => segmentCard(s))}
              </Box>
            );
          })}
        </Box>
        )}
      </Box>
  );

  // Preview + trim card ("inspector" in the unified workspace's shared stage).
  const inspectorSection = (
      <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
        {selected ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                  {selectedSegment?.name || selected.name}
                </Typography>
                {selectedSegment && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    Original: {selected.name}
                  </Typography>
                )}
              </Box>
              {!selectedSegment && usedSet.has(selected.fileId) && <OnTimelineChip />}
              {selectedSegment && usedSegmentSet.has(selectedSegment.segmentId) && <OnTimelineChip />}
              {/* Sits with the other clip-level actions rather than inside the trim panel
                  below: down there it was invisible until you scrolled past the trimmer, and
                  it was gated on the trim panel rendering at all. Filing a clip in the library
                  is a thing you do TO a clip, like extracting its audio — not a trim option.
                  Video only: the library is footage to lay under a voiceover. */}
              {/* Already a library clip — offer editing it there instead of filing a duplicate.
                  No tooltip: MUI renders one as a native `title`, which wins the accessible name,
                  so a screen reader would announce the explanation instead of "Edit library clip".
                  The trim panel below carries the this-project-vs-library distinction. */}
              {selectedBrollLabel && onEditBrollLibraryClip && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VideoLibraryIcon fontSize="small" />}
                  onClick={() => onEditBrollLibraryClip(selectedBrollLabel)}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Edit library clip…
                </Button>
              )}
              {!selectedBrollLabel && onAddToBrollLibrary && selected.mimeType.startsWith('video/') && (() => {
                return (
                  <Tooltip title={brollRange && !brollRange.whole
                    ? `Add ${secLabel(brollRange.inSec)}–${secLabel(brollRange.outSec)} to the B-Roll Library so it can be found later. Doesn't change the timeline.`
                    : 'Add this clip to the B-Roll Library so it can be found later. Trim first to add just part of it.'}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={brollSaving}
                        onClick={() => {
                          // Prefilled from what the labeler already decided about this clip,
                          // rather than asking a question that has been answered.
                          setBrollDraft({ ...EMPTY_CLIP_DRAFT, productCategory: labelledProduct });
                          setBrollError(null);
                          setBrollDialogOpen(true);
                        }}
                        startIcon={brollSaving ? <CircularProgress size={12} /> : <VideoLibraryIcon fontSize="small" />}
                      >
                        Add to B-Roll Library
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
              {selectedSegment && onAddSegment && !usedSegmentSet.has(selectedSegment.segmentId) && (
                <Button size="small" variant="outlined" startIcon={<PlaylistAddIcon fontSize="small" />} onClick={() => { void addSegmentToTimeline(selectedSegment); }}>
                  Add to timeline
                </Button>
              )}
              {!selectedSegment && onExtractAudio && selected.mimeType.startsWith('video/') && (() => {
                const previewReady = previewStatuses?.[selected.fileId] === 'ready';
                return (
                  <Tooltip title={previewReady
                    ? 'Save this clip’s audio as its own file, usable on audio tracks'
                    : 'Available once the preview finishes transcoding'}>
                    <span>
                      <Button size="small" variant="outlined" disabled={!previewReady || extractingAudio}
                        startIcon={extractingAudio ? <CircularProgress size={12} /> : <AudiotrackIcon fontSize="small" />}
                        onClick={extractSelectedAudio}>
                        Extract audio
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
              {!selectedSegment && onAdd && !usedSet.has(selected.fileId) && (
                <Button size="small" variant="outlined" startIcon={<PlaylistAddIcon fontSize="small" />} onClick={() => handleAdd([selected])}>
                  Add to timeline
                </Button>
              )}
              {/* A borrowed clip is dropped from the project, not deleted — the library keeps it,
                  and other projects may be using it. */}
              {selectedSegment && selectedBrollLabel && onRemoveBrollAsset && (
                <Tooltip title="Remove this clip from the project">
                  <IconButton
                    aria-label={`Remove ${selectedSegment.name || 'clip'} from this project`}
                    size="small"
                    color="error"
                    onClick={() => setRemoveTarget({
                      kind: 'broll', segment: selectedSegment, labelId: selectedBrollLabel.labelId,
                    })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Saved sub-clips are user-created (not Drive files), so they can
                  be deleted — but only here in the editor, never as a list trash-can. */}
              {selectedSegment && !selectedBrollLabel && onDeleteSegment && (
                <Tooltip title="Delete this saved clip">
                  <IconButton aria-label={`Delete saved clip ${selectedSegment.name || 'saved clip'}`} size="small" color="error" onClick={() => setRemoveTarget({ kind: 'segment', segment: selectedSegment })}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {/* The player and the (optional) note panel share one row — the video
                keeps the majority of the width so it stays the focus. */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Video plays from the transcoded copy, so only load the player once it's ready;
                audio (no status) plays natively and always loads. */}
            {selected.mimeType.startsWith('video/') && previewStatuses?.[selected.fileId] !== 'ready' ? (
              <Box sx={{ width: '100%', height: STAGE_PLAYER_HEIGHT, backgroundColor: '#f8fafc', border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary' }}>
                {previewStatuses?.[selected.fileId] === 'error' ? (
                  <>
                    <Typography variant="body2" color="error.light" sx={{ maxWidth: 560, textAlign: 'center' }}>
                      {previewErrors?.[selected.fileId] || 'Preview failed to prepare.'}
                    </Typography>
                    {onRetryPreview && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={retryingPreview ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
                        disabled={retryingPreview}
                        onClick={retrySelectedPreview}
                      >
                        Retry preview
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <CircularProgress size={28} />
                    <Typography variant="body2">Preparing preview…</Typography>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{
                position: 'relative', width: '100%', height: STAGE_PLAYER_HEIGHT, backgroundColor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                '&:hover .video-preview-playback-overlay': { opacity: 1 },
              }}>
                <Box
                  component="video"
                  ref={previewPlayerRef}
                  key={selected.streamUrl}
                  src={selected.streamUrl}
                  controls
                  preload="auto"
                  onCanPlay={() => setReadyPreviewSource(selectedPreviewSource)}
                  onPlay={() => setPreviewPlaying(true)}
                  onPause={() => setPreviewPlaying(false)}
                  onEnded={() => setPreviewPlaying(false)}
                  onLoadedMetadata={(e: React.SyntheticEvent<HTMLVideoElement>) => {
                    loadDuration(e.currentTarget.duration);
                  }}
                  sx={{ width: '100%', height: '100%', backgroundColor: '#fff', display: 'block', objectFit: 'contain' }}
                />
                <PreviewPlaybackOverlay playing={previewPlaying} onToggle={togglePreviewPlayback} />
              </Box>
            )}
            </Box>
            {notePanel && (
              <Box sx={{ flex: '0 0 30%', maxWidth: 340, minWidth: 220, display: 'flex' }}>
                {notePanel}
              </Box>
            )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {formatDuration(selected.durationSec)}
              {` · ${formatBytes(selected.sizeBytes)}`}
            </Typography>

            {/* Edit the linked timeline instance or save the selected range as a
                separate reusable clip. Both actions intentionally share one
                trimmer but remain separate buttons. */}
            {onSaveSegment && (() => {
              // Borrowed clips included: the page polls transcode status for them too, so this
              // agrees with the player gate above rather than showing a trimmer for something
              // that can't be played or thumbnailed yet.
              const canCapture = !selected.mimeType.startsWith('video/')
                || previewStatuses?.[selected.fileId] === 'ready';
              const dur = mediaDur;
              const valid = trimOut != null && trimOut > trimIn + 0.05;
              const baseIn = selectedSegment?.inSec ?? linkedTimelineClip?.inSec ?? 0;
              const baseOut = selectedSegment?.outSec ?? linkedTimelineClip?.outSec ?? dur ?? 0;
              const trimChanged = valid && (
                Math.abs(trimIn - baseIn) > 0.01
                || Math.abs((trimOut || 0) - baseOut) > 0.01
              );
              const onTimeline = usedSet.has(selected.fileId);
              const canTrim = selectedSegment ? !!onTrimSegment : !!onTrimSelected;
              const trimTooltip = selectedBrollLabel
                ? (trimChanged
                  ? 'Change what this clip covers in THIS project. The library clip is untouched.'
                  : 'Move a handle to choose a different trim')
                : selectedSegment
                ? (trimChanged ? 'Update this saved clip and any timeline copy' : 'Move a handle to choose a different trim')
                : onTimeline
                  ? (trimChanged ? 'Apply this range to the linked timeline clip' : 'Move a handle to choose a different trim')
                  : 'Trim adds this clip to the timeline with the chosen range';
              if (!canCapture) return null;
              return (
                <Box sx={{ mt: 1.5, p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <ContentCutIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {selectedBrollLabel ? 'Edit for this project' : selectedSegment ? 'Edit saved clip' : 'Edit clip'}
                    </Typography>
                    <Tooltip title="Drag the handles to frame it; playback loops inside the selection" placement="top">
                      <IconButton aria-label="How clip trimming works" size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ ml: 'auto' }}>
                      <TimelineZoomControls zoom={clipZoom} onChange={setClipZoom} label="Clip zoom" controlName="clips editor" />
                    </Box>
                  </Box>
                  {dur != null && trimOut != null ? (
                    <>
                      <RangeTrimmer
                        duration={dur}
                        inSec={trimIn}
                        outSec={trimOut}
                        playheadSec={playheadT}
                        onChange={handleTrimChange}
                        onSeek={seekTrimPlayhead}
                        pixelsPerSecond={clipZoom}
                        onZoomChange={setClipZoom}
                        focusRangeKey={rangeResetKey}
                        trackHeight={RANGE_PREVIEW_TRACK_HEIGHT}
                        videoFrames={rangePreviewFrames}
                        loadVideoFrames={previewCanPlay}
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {secLabel(trimIn)} → {secLabel(trimOut)} ({(trimOut - trimIn).toFixed(1)}s)
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title={trimTooltip}>
                          <span>
                            <Button size="small" variant="outlined" disabled={!canTrim || !trimChanged || trimSaving} onClick={trimCurrentClip}
                              startIcon={trimSaving ? <CircularProgress size={12} /> : <CropIcon fontSize="small" />}>
                              Trim
                            </Button>
                          </span>
                        </Tooltip>
                        {/* Not offered for a borrowed clip: a saved sub-clip belongs to the
                            project, and carving one out of another creator's file would enrol
                            that file in this plan's own clip list. Trim it, or make a second
                            library clip from the library page. */}
                        {!selectedBrollLabel && (
                          <Tooltip title="Save this range as a reusable clip without changing the timeline clip">
                            <span>
                              <Button size="small" variant="contained" disabled={!valid || segSaving}
                                onClick={() => { setSegName(''); setSaveError(null); setNameDialogOpen(true); }}
                                startIcon={segSaving ? <CircularProgress size={12} color="inherit" /> : <ContentCutIcon fontSize="small" />}>
                                Save as new clip
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {/* "Add to B-Roll Library" deliberately lives in the header action
                            row above, not here — see the comment there. */}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Loading duration…</Typography>
                  )}
                </Box>
              );
            })()}

            {/* AI labels for this clip — what the b-roll library is searched by.
                Hidden entirely until the page has loaded them, so a plan opened
                before labeling exists looks exactly as it did before. */}
            {/* Not for borrowed clips: `clipLabels` is keyed by the project's own files, so this
                panel would offer to create a second label on a file the library already has one
                for. "Edit library clip…" in the header edits the real one. */}
            {clipLabels && onSaveClipLabel && !selectedBrollLabel && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E2E8F0' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 1 }}>
                  Labels
                </Typography>
                <ClipLabelEditor
                  labels={clipLabels[selected.fileId] || []}
                  vocabulary={clipLabelVocabulary || null}
                  categories={clipCategories}
                  onCreateCategory={onCreateClipCategory}
                  loading={clipLabelsLoading}
                  onSave={onSaveClipLabel}
                />
              </Box>
            )}
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">Select a clip to preview and scrub it.</Typography>
          </Paper>
        )}
      </Box>
  );

  return (
    <>
    {slots ? (
      <>
        {slots.library ? createPortal(librarySection, slots.library) : null}
        {slots.inspector ? createPortal(inspectorSection, slots.inspector) : null}
      </>
    ) : (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {librarySection}
        {inspectorSection}
      </Box>
    )}
    <SaveClipDialog
      open={nameDialogOpen}
      kind={selected?.mimeType.startsWith('audio/') ? 'audio' : 'video'}
      name={segName}
      saving={segSaving}
      error={saveError}
      onNameChange={setSegName}
      onCancel={() => setNameDialogOpen(false)}
      onSave={saveSegment}
    />
    {/* Naming and filing the range for the LIBRARY, not the timeline — hence its own
        dialog rather than SaveClipDialog: the useful thing to capture here is what
        someone searching the library later would look for. */}
    <Dialog open={brollDialogOpen} onClose={() => { if (!brollSaving) setBrollDialogOpen(false); }} maxWidth="xs" fullWidth>
      <DialogTitle>Add to the B-Roll Library</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {selected?.name}
          {/* Say which it is, so nobody files a whole take thinking they filed their trim. */}
          {brollRange && (brollRange.whole
            ? ' · the whole clip (trim first to add just part of it)'
            : ` · ${secLabel(brollRange.inSec)} → ${secLabel(brollRange.outSec)}`)}
        </Typography>
        {/* The library's own fields, not a smaller copy of them: a clip filed from here is
            searched by the same page and needs the same things said about it. Details are
            folded away because filing is a detour from editing, not the job. */}
        <ClipLabelFields
          draft={brollDraft}
          onDraftChange={setBrollDraft}
          vocabulary={clipLabelVocabulary ?? null}
          categories={clipCategories}
          onCreateCategory={onCreateClipCategory}
          autoFocusName
          showConfidence={false}
          detailsCollapsible
        />
        {/* Not merely "hard to find": the library refuses to search until a product is
            chosen, and a null one appears in no product list — so a clip filed without one
            cannot be reached from that page at all. */}
        {!brollDraft.productCategory && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Pick a product — the library is browsed product-first, so a clip without one can
            never be found there.
          </Typography>
        )}
        {brollError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
            {brollError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBrollDialogOpen(false)} disabled={brollSaving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={addToBrollLibrary}
          disabled={brollSaving || !brollRange || !brollDraft.productCategory}
        >
          {brollSaving ? <CircularProgress size={16} color="inherit" /> : 'Add to library'}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={!!removeTarget} onClose={() => { if (!removing) setRemoveTarget(null); }} maxWidth="xs" fullWidth>
      <DialogTitle>
        {removeTarget?.kind === 'broll' ? 'Remove this b-roll from the project?' : 'Delete saved clip?'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {removeTarget && (removeTarget.kind === 'broll'
            ? `“${removeTarget.segment.name || 'This clip'}” will be taken out of this project${usedSegmentSet.has(removeTarget.segment.segmentId) ? ', including from the timeline' : ''}. It stays in the b-roll library, and any other project using it is unaffected.`
            : `“${removeTarget.segment.name || 'Saved clip'}” will be permanently deleted${usedSegmentSet.has(removeTarget.segment.segmentId) ? ' and removed from the timeline' : ''}. The source clip stays in your library.`)}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRemoveTarget(null)} disabled={removing}>Cancel</Button>
        <Button color="error" variant="contained" onClick={confirmRemoval} disabled={removing}>
          {removing
            ? <CircularProgress size={16} color="inherit" />
            : removeTarget?.kind === 'broll' ? 'Remove from project' : 'Delete clip'}
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!snack}
      autoHideDuration={2000}
      onClose={() => setSnack(null)}
      message={snack}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
    </>
  );
}
